// JoinCourseModal.jsx - Modal for students to join courses with 6-digit code
import React, { useState, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import { getApiUrl } from '../utils/api';
import '../styles/joinCourseModal.css';

const JoinCourseModal = ({ isVisible, onClose, onSuccess }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef([]);

  const handleChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('Text');
    const digits = pasteData.replace(/\D/g, '').split('').slice(0, 6);
    
    const newCode = [...code];
    digits.forEach((digit, i) => {
      if (i < 6) newCode[i] = digit;
    });
    setCode(newCode);
    
    // Focus on the next empty input or the last one
    const nextEmptyIndex = newCode.findIndex(c => c === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : Math.min(nextEmptyIndex, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const invitationCode = code.join('');
    
    if (invitationCode.length !== 6) {
      setError('Please enter a complete 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/api/course/join'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invitation_code: invitationCode })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        if (data.already_member) {
          // User is already a member, show friendly message and close modal
          setError(''); // Clear any existing errors
          alert(`You are already enrolled in ${data.course.course_name}!`);
          onSuccess(data.course);
        } else {
          // Successfully joined new course
          onSuccess(data.course);
        }
      } else {
        setError(data.message || 'Failed to join course. Please check your code.');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="join-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Join a Course</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        <div className="modal-body">
          <p>Enter the 6-digit course code provided by your instructor:</p>
          
          <form onSubmit={handleSubmit}>
            <div className="code-input-group">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={e => handleChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="code-input"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="modal-actions">
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={onClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="join-btn"
                disabled={isLoading || code.join('').length !== 6}
              >
                {isLoading ? 'Joining...' : 'Join Course'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JoinCourseModal;
